import { create } from "zustand";
import {
  getWhatsAppAccount,
  getConfirmations,
  getConversation,
  getReportsSummary,
  type WhatsAppAccount,
  type Confirmation,
  type Conversation,
  type ConfirmationStatus,
  type ReportsSummary,
} from "../services/whatsapp/whatsappService";

export type FilterTab = "all" | ConfirmationStatus;

interface ChatState {
  // State
  whatsappAccount: WhatsAppAccount | null;
  accountLoading: boolean;
  accountChecked: boolean;
  confirmations: Confirmation[];
  confirmationsLoading: boolean;
  selectedConfirmation: Confirmation | null;
  conversation: Conversation | null;
  conversationLoading: boolean;
  activeFilter: FilterTab;
  searchQuery: string;
  summary: ReportsSummary | null;

  // Actions
  fetchAccount: (ownerId: string) => Promise<void>;
  fetchConfirmations: (
    ownerId: string,
    status?: ConfirmationStatus
  ) => Promise<void>;
  selectConversation: (confirmation: Confirmation) => Promise<void>;
  setFilter: (filter: FilterTab) => void;
  setSearchQuery: (query: string) => void;
  fetchSummary: (ownerId: string) => Promise<void>;
  reset: () => void;
}

export const useChatStore = create<ChatState>((set) => ({
  whatsappAccount: null,
  accountLoading: false,
  accountChecked: false,
  confirmations: [],
  confirmationsLoading: false,
  selectedConfirmation: null,
  conversation: null,
  conversationLoading: false,
  activeFilter: "all",
  searchQuery: "",
  summary: null,

  fetchAccount: async (ownerId: string) => {
    set({ accountLoading: true });
    try {
      const account = await getWhatsAppAccount(ownerId);
      if ((account as any).error) {
        set({
          whatsappAccount: null,
          accountLoading: false,
          accountChecked: true,
        });
        return;
      }
      set({
        whatsappAccount: account,
        accountLoading: false,
        accountChecked: true,
      });
    } catch {
      set({
        whatsappAccount: null,
        accountLoading: false,
        accountChecked: true,
      });
    }
  },

  fetchConfirmations: async (
    ownerId: string,
    status?: ConfirmationStatus
  ) => {
    set({ confirmationsLoading: true });
    try {
      const confirmations = await getConfirmations(ownerId, status);
      set({ confirmations, confirmationsLoading: false });
    } catch {
      set({ confirmations: [], confirmationsLoading: false });
    }
  },

  selectConversation: async (confirmation: Confirmation) => {
    set({
      selectedConfirmation: confirmation,
      conversationLoading: true,
    });
    try {
      const conversation = await getConversation(confirmation.id);
      if ((conversation as any).error) {
        set({ conversation: null, conversationLoading: false });
        return;
      }
      set({ conversation, conversationLoading: false });
    } catch {
      set({ conversation: null, conversationLoading: false });
    }
  },

  setFilter: (filter: FilterTab) => set({ activeFilter: filter }),
  setSearchQuery: (query: string) => set({ searchQuery: query }),

  fetchSummary: async (ownerId: string) => {
    try {
      const summary = await getReportsSummary(ownerId);
      set({ summary });
    } catch {
      set({ summary: null });
    }
  },

  reset: () =>
    set({
      whatsappAccount: null,
      accountChecked: false,
      confirmations: [],
      selectedConfirmation: null,
      conversation: null,
      activeFilter: "all",
      searchQuery: "",
      summary: null,
    }),
}));
